/**
 * Test (notification) feature types.
 *
 * Form values exactly match the Angular `email-test.component.ts` shape
 * so the payload transform can stay close to the reference. Payload
 * types reflect what the backend `/v1/notifications/{channel}` endpoint
 * actually accepts (date+time merged into ISO datetime, variables
 * flattened to a record, attachments base64-encoded).
 */

export type NotificationChannel =
  | 'email'
  | 'sms'
  | 'whatsapp'
  | 'telegram'
  | 'web-push'
  | 'app-push';

export type RecipientType = 'INDIVIDUAL' | 'GROUP';
export type MessageType = 'TEMPLATE' | 'CUSTOM';
export type MessagePriority = 'CRITICAL' | 'STANDARD';
export type ScheduleType = 'NONE' | 'ONCE' | 'RECURRING';
export type IntervalUnit =
  | 'MINUTE'
  | 'HOUR'
  | 'DAY'
  | 'WEEK'
  | 'MONTH'
  | 'YEAR';

export interface VariableEntry {
  key: string;
  value: string;
}

export interface AddressEntry {
  value: string;
}

export interface RecipientsValues {
  type: RecipientType;
  // Wrapped in objects (rather than `string[]`) so `useFieldArray`
  // can assign each row a stable id — RHF needs object entries to
  // make append/remove update the rendered list.
  addresses: AddressEntry[];
  groupId: string;
}

export type WhatsappHeaderMediaType = 'IMAGE' | 'DOCUMENT' | 'VIDEO' | 'AUDIO';
export type WhatsappHeaderMediaSource = 'URL' | 'UPLOAD';

export interface WhatsappHeaderMediaValues {
  type: WhatsappHeaderMediaType | '';
  source: WhatsappHeaderMediaSource;
  link: string;
  fileName: string;
  fileBase64: string;
  mimeType: string;
}

export interface MessageValues {
  type: MessageType;
  // CUSTOM-only fields
  subject: string;
  content: string;
  // TEMPLATE-only fields
  templateId: string;

  metaTemplateId: string;
  templateType: string;
  language: string;
  variables: VariableEntry[];
  headerTextVariables: VariableEntry[];
  headerMedia: WhatsappHeaderMediaValues;
  customMedia: WhatsappHeaderMediaValues;
}

export interface RecurringValues {
  intervalValue: number;
  intervalUnit: IntervalUnit | '';
  expiresDate: string | null;
  expiresTime: string;
}

export interface ScheduleValues {
  type: ScheduleType;
  date: string | null;
  time: string;
  recurring: RecurringValues;
  timezone: string;
}

export type AttachmentMimeType =
  | 'application/pdf'
  | 'text/plain'
  | `image/${string}`;

export type AttachmentDispositionWire = 'inline' | 'attachment';

export const isSupportedAttachmentMime = (
  mime: string
): mime is AttachmentMimeType =>
  mime === 'application/pdf' ||
  mime === 'text/plain' ||
  mime.startsWith('image/');

export interface SelectedFile {
  name: string;
  type: string;
  base64: string;
  inline?: boolean;
  contentId?: string;
}

export interface NotificationTestFormValues {
  accountId: string;
  appId: string;
  messagePriority: MessagePriority;
  webhookUrl: string;
  recipients: RecipientsValues;
  message: MessageValues;
  schedule: ScheduleValues;
  attachments: SelectedFile[];
}

// ── API response shapes ────────────────────────────────────────────

export interface AccountListItem {
  id: string;
  accountName: string;
}

export interface AccountProviderListItem {
  id: string;
  providerName: string;
  communicationType: string[];
  providerStatus: string;
  priority: number;
  retries: number;
  providerType: number;
}

export type WhatsAppProviderType = 'twilio' | 'meta';

export interface WhatsAppProviderConfigForAccount {
  available: WhatsAppProviderType[];
}

export interface TemplateOption {
  id: string;
  templateName: string;
  language?: string;
  templateType?: string;
}

export interface MetaWhatsappTemplate {
  id?: string;
  name?: string;
  language?: string;
  status?: string;
  category?: string;
  components?: unknown;
}

export const getTemplateHeaderType = (
  t: MetaWhatsappTemplate | undefined,
): WhatsappHeaderMediaType | 'TEXT' | 'NONE' => {
  const components = t?.components;
  if (!Array.isArray(components)) return 'NONE';
  for (const c of components as Array<Record<string, unknown>>) {
    if (
      c &&
      typeof c === 'object' &&
      typeof c.type === 'string' &&
      c.type.toUpperCase() === 'HEADER'
    ) {
      const format =
        typeof c.format === 'string' ? c.format.toUpperCase() : '';
      if (format === 'IMAGE' || format === 'DOCUMENT' || format === 'VIDEO') {
        return format;
      }
      return 'TEXT';
    }
  }
  return 'NONE';
};

export const extractPositionalVariables = (
  t: MetaWhatsappTemplate | undefined,
): { headerVars: MetaTemplateVariable[]; bodyVars: MetaTemplateVariable[] } => {
  const out = {
    headerVars: [] as MetaTemplateVariable[],
    bodyVars: [] as MetaTemplateVariable[],
  };
  const components = t?.components;
  if (!Array.isArray(components)) return out;

  for (const cRaw of components as Array<Record<string, unknown>>) {
    if (!cRaw || typeof cRaw !== 'object') continue;
    const type = typeof cRaw.type === 'string' ? cRaw.type.toUpperCase() : '';
    if (type !== 'BODY' && type !== 'HEADER') continue;

    if (type === 'HEADER') {
      const format =
        typeof cRaw.format === 'string' ? cRaw.format.toUpperCase() : '';
      if (format !== 'TEXT') continue;
    }

    const text = typeof cRaw.text === 'string' ? cRaw.text : '';
    if (text === '') continue;

    const matches = text.match(/\{\{(\d+)\}\}/g);
    if (!matches) continue;

    const example = (cRaw.example ?? {}) as Record<string, unknown>;
    const bodyTextRow = Array.isArray(example.body_text)
      ? ((example.body_text as unknown[])[0] as unknown[] | undefined)
      : undefined;
    const headerTextRow = Array.isArray(example.header_text)
      ? (example.header_text as unknown[])
      : undefined;

    const seen = new Set<string>();
    for (const m of matches) {
      const idxStr = m.slice(2, -2); // strip "{{" "}}"
      if (seen.has(idxStr)) continue;
      seen.add(idxStr);

      const idxNum = parseInt(idxStr, 10);
      let exampleValue: string | undefined;
      if (type === 'BODY' && bodyTextRow && Array.isArray(bodyTextRow)) {
        const v = bodyTextRow[idxNum - 1];
        if (typeof v === 'string') exampleValue = v;
      } else if (type === 'HEADER' && headerTextRow) {
        const v = headerTextRow[idxNum - 1];
        if (typeof v === 'string') exampleValue = v;
      }

      const entry: MetaTemplateVariable = {
        paramName: idxStr,
        example: exampleValue ?? null,
        component: type,
      };
      if (type === 'BODY') out.bodyVars.push(entry);
      else out.headerVars.push(entry);
    }
  }

  const byIndex = (a: MetaTemplateVariable, b: MetaTemplateVariable) =>
    parseInt(a.paramName, 10) - parseInt(b.paramName, 10);
  out.bodyVars.sort(byIndex);
  out.headerVars.sort(byIndex);
  return out;
};

export interface TemplateVariable {
  variable_name: string;
}

export interface MetaTemplateVariable {
  paramName: string;
  example?: string | null;
  component: string; // "BODY" or "HEADER"
}

export interface MetaTemplateVariablesResult {
  success: boolean;
  errorCode?: string | null;
  message?: string | null;
  templateName?: string | null;
  language?: string | null;
  parameterFormat?: string | null; // "NAMED" or "POSITIONAL"
  variables: MetaTemplateVariable[];
}

export interface NotificationTestResponse {
  success: boolean;
  message?: string;
}

// ── Payload to backend ────────────────────────────────────────────

export interface NotificationTestPayload {
  accountId: string;
  appId: string;
  messageType: MessagePriority;
  webhookUrl: string;
  recipients:
    | { type: 'INDIVIDUAL'; addresses: string[] }
    | { type: 'GROUP'; addresses: string[]; groupId: string }
    | { type: 'INDIVIDUAL'; numbers: string[] }
    | { type: 'GROUP'; numbers: string[]; groupId: string }
    | { type: 'INDIVIDUAL'; chatIds: string[] }
    | { type: 'GROUP'; chatIds: string[]; groupId: string };
  message: {
    type: MessageType;
    templateId?: string;
    metaTemplateId?: string;
    templateType?: string;
    templateReturnType?: string;
    language?: string;
    variables?: Record<string, string>;
    headerTextVariables?: Record<string, string>;
    subject?: string;
    content?: string;
    attachments?: Array<{
      name: string;
      content: string;
      type: AttachmentMimeType;
      disposition?: AttachmentDispositionWire;
      contentId?: string;
    }>;

    headerMedia?: {
      type: WhatsappHeaderMediaType;
      link?: string;
      fileName?: string;
      fileBase64?: string;
      mimeType?: string;
    };

    customMedia?: {
      type: WhatsappHeaderMediaType;
      link?: string;
      fileName?: string;
      fileBase64?: string;
      mimeType?: string;
    };
  };
  schedule?: {
    type: 'ONCE' | 'RECURRING';
    datetime?: string;
    timezone: string;
    recurring: {
      interval: { value: number; unit: IntervalUnit | '' };
      expiresAt?: string;
    };
  };
}
